import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

const Masked = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { n: "5000+", l: "Brands Since 2015" },
  { n: "20,000+", l: "Projects Delivered" },
  { n: "12+", l: "Years of Building" },
  { n: "100%", l: "Founder-Led" },
];

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-32 pb-16">
      {/* floating glow orbs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/3 right-[-6%] h-[70vh] w-[46vw] max-w-[820px] rounded-full blur-[90px] opacity-70 animate-floaty"
          style={{ background: "radial-gradient(circle at 42% 50%, rgba(198,245,63,0.16) 0%, transparent 60%)" }} />
        <div className="absolute top-[8%] left-[-10%] h-[52vh] w-[36vw] rounded-full blur-[120px] opacity-30 animate-floaty"
          style={{ background: "radial-gradient(circle, rgba(198,245,63,0.5) 0%, transparent 65%)", animationDelay: "-3s" }} />
        <div className="absolute bottom-[-10%] left-[30%] h-[40vh] w-[32vw] rounded-full blur-[110px] opacity-25 animate-floaty"
          style={{ background: "radial-gradient(circle, rgba(124,255,178,0.5) 0%, transparent 62%)", animationDelay: "-5s" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="flex items-center gap-3 mb-9"
        >
          <span className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse-dot" />
          <span className="font-mono2 text-[10px] sm:text-xs tracking-[0.3em] text-[#8A8C7E] uppercase">
            Sanvalio Growth Studio — Strategy / Design / Growth
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight leading-[0.95] text-[12.5vw] sm:text-[9vw] lg:text-[6.8rem] text-[#F2F1E8]">
          <Masked delay={0.25}>Your brand</Masked>
          <Masked delay={0.4}>is leaking</Masked>
          <Masked delay={0.55}>
            <span className="text-accent-gradient">money every day.</span>
          </Masked>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-7 max-w-xl text-sm sm:text-lg text-[#B9BBAC] leading-relaxed"
        >
          Not because your product is bad — because the system around it is missing.
          In one 60-minute Growth Call, find exactly where the leaks are and get a
          roadmap to fix them. Serious founders only.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => navigate("/book?plan=growth")}
            className="btn-accent animate-pulse-glow group flex items-center gap-3 font-semibold text-sm sm:text-base px-8 py-4"
          >
            Book Your ₹999 Growth Call
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          <Link
            to="/audit"
            data-testid="hero-audit-btn"
            className="group flex items-center gap-2 border border-[#27331C] text-[#F2F1E8] text-sm sm:text-base px-7 py-4 rounded-full hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all duration-300 hover:scale-[1.03]"
          >
            <Sparkles className="w-4 h-4" />
            Get Free Brand Audit
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-5 font-mono2 text-[10px] sm:text-xs tracking-[0.15em] text-[#8A8C7E] uppercase"
        >
          ₹999 fully adjusted when we execute together · Only 5 slots a week
        </motion.p>

        {/* glass stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          data-testid="hero-stats"
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl rounded-2xl glass px-6 py-6"
        >
          {STATS.map((s) => (
            <div key={s.l}>
              <div className="font-display text-3xl sm:text-4xl font-bold text-accent-gradient">{s.n}</div>
              <div className="mt-1.5 font-mono2 text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-[#8A8C7E]">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
